// ============================================================================
// CUSTOMER RESEARCH GRAPH
// Rich interactive force-directed knowledge graph viewer
// Features: shaped nodes, dark background, curved colored edges, edge labels,
// hover highlighting, tooltips, minimap, right-click menu, double-click focus,
// fullscreen, zoom-to-fit, stats overlay, confidence badges, type clustering
// ============================================================================

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  Search, Loader2, X, Filter, Maximize2, Minimize2,
  Focus, EyeOff, Eye, RotateCcw, Scan, GitMerge, AlertTriangle, Pencil, Check, Layers
} from 'lucide-react';
import {
  customerResearchService,
  GraphData,
  GraphNode
} from '../../services/sessionTasksService';
import {
  getNodeConfig,
  getNodeDisplayName,
  getEdgeColor,
  getLinkEndpointId,
  drawNode,
  drawNodeHitArea,
  drawEdgeLabel,
  drawTooltip,
  drawMinimap,
  DrawNodeOpts
} from './customerResearchGraphHelpers';
import { DuplicatesMergePanel } from './DuplicatesMergePanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CustomerResearchGraphProps {
  projectId: string;
  sessionId?: string;
  allowMerge?: boolean;
}

interface ForceGraphNode extends GraphNode {
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  __connections?: number;
}

interface ForceGraphLink {
  source: any;
  target: any;
  type: string;
  id: string;
}

interface ContextMenuState {
  node: ForceGraphNode;
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CustomerResearchGraph: React.FC<CustomerResearchGraphProps> = ({
  projectId,
  sessionId,
  allowMerge = false
}) => {
  // Data state
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Controls
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  // Interaction state
  const [selectedNode, setSelectedNode] = useState<ForceGraphNode | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [hiddenNodeIds, setHiddenNodeIds] = useState<Set<string>>(new Set());

  // Merge state
  const [mergeTargetNode, setMergeTargetNode] = useState<ForceGraphNode | null>(null);
  const [merging, setMerging] = useState(false);

  // Duplicate detection panel
  const [showDuplicatesPanel, setShowDuplicatesPanel] = useState(false);

  // Edit name state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [saveNameError, setSaveNameError] = useState<string | null>(null);

  // Refs for canvas callbacks (avoid stale closures)
  const hoveredNodeRef = useRef<ForceGraphNode | null>(null);
  const selectedNodeRef = useRef<ForceGraphNode | null>(null);
  const highlightedNeighboursRef = useRef<Set<string>>(new Set());
  const highlightedLinksRef = useRef<Set<string>>(new Set());
  const viewportRef = useRef({ k: 1, x: 0, y: 0 });

  // DOM refs
  const graphRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });

  // Double-click detection
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastClickedRef = useRef<string | null>(null);

  // Keep selectedNodeRef in sync; reset edit state on node change
  useEffect(() => {
    selectedNodeRef.current = selectedNode;
    setIsEditingName(false);
    setSaveNameError(null);
  }, [selectedNode]);

  // -----------------------------------------------------------------------
  // Measure container dimensions
  // -----------------------------------------------------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const update = () => {
      setDimensions({
        width: container.clientWidth,
        height: Math.max(container.clientHeight, 500)
      });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, [isFullscreen]);

  // -----------------------------------------------------------------------
  // Fetch graph data
  // -----------------------------------------------------------------------
  const loadGraphData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await customerResearchService.getGraphData(projectId, { sessionId });
      setGraphData(data);
      const types = new Set(data.nodes.map(n => n.type));
      setActiveFilters(types);
    } catch (err: any) {
      setError('Failed to load graph data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [projectId, sessionId]);

  useEffect(() => { loadGraphData(); }, [loadGraphData]);

  // -----------------------------------------------------------------------
  // Computed: entity types, connection counts
  // -----------------------------------------------------------------------
  const entityTypes = useMemo(() => {
    if (!graphData) return [];
    return Array.from(new Set(graphData.nodes.map(n => n.type))).sort();
  }, [graphData]);

  const connectionCounts = useMemo(() => {
    if (!graphData) return new Map<string, number>();
    const counts = new Map<string, number>();
    graphData.edges.forEach(edge => {
      counts.set(edge.source, (counts.get(edge.source) || 0) + 1);
      counts.set(edge.target, (counts.get(edge.target) || 0) + 1);
    });
    return counts;
  }, [graphData]);

  // -----------------------------------------------------------------------
  // Filtered & prepared graph data for ForceGraph2D
  // -----------------------------------------------------------------------
  const forceGraphData = useMemo(() => {
    if (!graphData) return { nodes: [], links: [] };
    const lowerQuery = searchQuery.toLowerCase();

    const filteredNodes: ForceGraphNode[] = graphData.nodes
      .filter(node => activeFilters.has(node.type))
      .filter(node => !hiddenNodeIds.has(node.id))
      .filter(node => !searchQuery || (node.name || node.avatarName || node.story || node.text || node.id).toLowerCase().includes(lowerQuery))
      .map(node => ({
        ...node,
        __connections: connectionCounts.get(node.id) || 0
      }));

    const filteredNodeIds = new Set(filteredNodes.map(n => n.id));

    const filteredLinks: ForceGraphLink[] = graphData.edges
      .filter(edge => filteredNodeIds.has(edge.source) && filteredNodeIds.has(edge.target))
      .map(edge => ({
        source: edge.source,
        target: edge.target,
        type: edge.type,
        id: edge.id
      }));

    return { nodes: filteredNodes, links: filteredLinks };
  }, [graphData, activeFilters, searchQuery, connectionCounts, hiddenNodeIds, sessionId]);

  // -----------------------------------------------------------------------
  // Per-type counts (for stats overlay)
  // -----------------------------------------------------------------------
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    forceGraphData.nodes.forEach(n => {
      counts[n.type] = (counts[n.type] || 0) + 1;
    });
    return counts;
  }, [forceGraphData.nodes]);

  // -----------------------------------------------------------------------
  // Fullscreen dimensions
  // -----------------------------------------------------------------------
  const [fsSize, setFsSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    if (!isFullscreen) return;
    const onResize = () => setFsSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isFullscreen]);

  // -----------------------------------------------------------------------
  // Graph width accounting for sidebar
  // -----------------------------------------------------------------------
  const graphWidth = useMemo(() => {
    if (isFullscreen) return fsSize.width;
    return selectedNode ? Math.max(dimensions.width - 340, 400) : dimensions.width;
  }, [dimensions.width, selectedNode, isFullscreen, fsSize.width]);

  const graphHeight = useMemo(() => {
    // In fullscreen, subtract ~44px for the controls bar
    if (isFullscreen) return fsSize.height - 44;
    return Math.max(dimensions.height, 500);
  }, [dimensions.height, isFullscreen, fsSize.height]);

  // -----------------------------------------------------------------------
  // Toggle filter
  // -----------------------------------------------------------------------
  const toggleFilter = (type: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // -----------------------------------------------------------------------
  // Hover handler — highlight neighbourhood
  // -----------------------------------------------------------------------
  const handleNodeHover = useCallback((node: any | null) => {
    hoveredNodeRef.current = node || null;
    if (node) {
      const neighbours = new Set<string>([node.id]);
      const links = new Set<string>();
      forceGraphData.links.forEach(link => {
        const srcId = getLinkEndpointId(link.source);
        const tgtId = getLinkEndpointId(link.target);
        if (srcId === node.id || tgtId === node.id) {
          neighbours.add(srcId);
          neighbours.add(tgtId);
          links.add(link.id);
        }
      });
      highlightedNeighboursRef.current = neighbours;
      highlightedLinksRef.current = links;
    } else {
      highlightedNeighboursRef.current = new Set();
      highlightedLinksRef.current = new Set();
    }
  }, [forceGraphData.links]);

  // -----------------------------------------------------------------------
  // Click handler (with double-click detection)
  // -----------------------------------------------------------------------
  const handleDoubleClick = useCallback((node: any) => {
    setSelectedNode(node);
    // Zoom to this node's neighbourhood
    const neighbours = new Set<string>([node.id]);
    forceGraphData.links.forEach(link => {
      const srcId = getLinkEndpointId(link.source);
      const tgtId = getLinkEndpointId(link.target);
      if (srcId === node.id || tgtId === node.id) {
        neighbours.add(srcId);
        neighbours.add(tgtId);
      }
    });
    graphRef.current?.zoomToFit(400, 80, (n: any) => neighbours.has(n.id));
  }, [forceGraphData.links]);

  const handleNodeClick = useCallback((node: any) => {
    if (contextMenu) { setContextMenu(null); return; }

    if (lastClickedRef.current === node.id && clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      lastClickedRef.current = null;
      handleDoubleClick(node);
    } else {
      lastClickedRef.current = node.id;
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        setSelectedNode(prev => (prev?.id === node.id ? null : node));
        clickTimerRef.current = null;
        lastClickedRef.current = null;
      }, 250);
    }
  }, [handleDoubleClick, contextMenu]);

  // -----------------------------------------------------------------------
  // Right-click context menu
  // -----------------------------------------------------------------------
  const handleNodeRightClick = useCallback((node: any, event: MouseEvent) => {
    event.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    setContextMenu({
      node,
      x: event.clientX - (rect?.left || 0),
      y: event.clientY - (rect?.top || 0),
    });
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleBackgroundRightClick = useCallback((_event: MouseEvent) => {
    setContextMenu(null);
  }, []);

  // Context menu actions
  const ctxFocus = useCallback(() => {
    if (contextMenu) { handleDoubleClick(contextMenu.node); setContextMenu(null); }
  }, [contextMenu, handleDoubleClick]);

  const ctxHide = useCallback(() => {
    if (contextMenu) {
      setHiddenNodeIds(prev => new Set([...prev, contextMenu.node.id]));
      if (selectedNode?.id === contextMenu.node.id) setSelectedNode(null);
      setContextMenu(null);
    }
  }, [contextMenu, selectedNode]);

  const ctxShowOnlyConnections = useCallback(() => {
    if (!contextMenu || !graphData) return;
    const keep = new Set<string>([contextMenu.node.id]);
    graphData.edges.forEach(edge => {
      if (edge.source === contextMenu.node.id || edge.target === contextMenu.node.id) {
        keep.add(edge.source);
        keep.add(edge.target);
      }
    });
    const allIds = new Set(graphData.nodes.map(n => n.id));
    const toHide = new Set<string>();
    allIds.forEach(id => { if (!keep.has(id)) toHide.add(id); });
    setHiddenNodeIds(toHide);
    setContextMenu(null);
    // Zoom to fit visible nodes
    setTimeout(() => graphRef.current?.zoomToFit(400, 60), 100);
  }, [contextMenu, graphData]);

  const ctxShowAll = useCallback(() => {
    setHiddenNodeIds(new Set());
    setContextMenu(null);
    setTimeout(() => graphRef.current?.zoomToFit(400, 60), 100);
  }, []);

  // Merge: mark context menu node as target to merge into selectedNode
  const ctxMergeIntoSelected = useCallback(() => {
    if (contextMenu && selectedNode && contextMenu.node.id !== selectedNode.id) {
      setMergeTargetNode(contextMenu.node);
      setContextMenu(null);
    }
  }, [contextMenu, selectedNode]);

  const handleConfirmMerge = useCallback(async () => {
    if (!selectedNode || !mergeTargetNode) return;
    setMerging(true);
    try {
      await customerResearchService.mergeNodes(projectId, selectedNode.id, mergeTargetNode.id);
      setMergeTargetNode(null);
      setSelectedNode(null);
      await loadGraphData();
    } catch (err: any) {
      alert('Merge failed: ' + (err.message || 'Unknown error'));
    } finally {
      setMerging(false);
    }
  }, [selectedNode, mergeTargetNode, projectId, loadGraphData]);

  const handleStartEditName = useCallback(() => {
    if (!selectedNode) return;
    setEditedName(String(getNodeDisplayName(selectedNode)));
    setSaveNameError(null);
    setIsEditingName(true);
  }, [selectedNode]);

  const handleSaveName = useCallback(async () => {
    if (!selectedNode || !editedName.trim()) return;
    setSavingName(true);
    setSaveNameError(null);
    try {
      await customerResearchService.updateNodeName(projectId, selectedNode.id, editedName.trim());
      // Optimistically update local state
      const newName = editedName.trim();
      setGraphData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          nodes: prev.nodes.map(n => n.id === selectedNode.id ? { ...n, name: newName } : n)
        };
      });
      setSelectedNode(prev => prev ? { ...prev, name: newName } : prev);
      setIsEditingName(false);
    } catch (err: any) {
      setSaveNameError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSavingName(false);
    }
  }, [selectedNode, editedName, projectId]);

  // Close context menu on Escape & exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (contextMenu) setContextMenu(null);
        else if (isFullscreen) setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [contextMenu, isFullscreen]);

  // -----------------------------------------------------------------------
  // Canvas rendering callbacks
  // -----------------------------------------------------------------------
  const nodeCanvasObject = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const hNeighbours = highlightedNeighboursRef.current;
    const opts: DrawNodeOpts = {
      isSelected: selectedNodeRef.current?.id === node.id,
      isHovered: hoveredNodeRef.current?.id === node.id,
      isDimmed: hNeighbours.size > 0 && !hNeighbours.has(node.id),
    };
    drawNode(node, ctx, globalScale, opts);
  }, []);

  const nodePointerAreaPaint = useCallback((node: any, color: string, ctx: CanvasRenderingContext2D) => {
    drawNodeHitArea(node, color, ctx);
  }, []);

  const linkCanvasObject = useCallback((link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const hLinks = highlightedLinksRef.current;
    const isDimmed = hLinks.size > 0 && !hLinks.has(link.id);
    drawEdgeLabel(link, ctx, globalScale, isDimmed);
  }, []);

  const linkColorFn = useCallback((link: any) => {
    const hLinks = highlightedLinksRef.current;
    if (hLinks.size > 0 && !hLinks.has(link.id)) return 'rgba(71, 85, 105, 0.12)';
    return getEdgeColor(link.type);
  }, []);

  const linkWidthFn = useCallback((link: any) => {
    const hLinks = highlightedLinksRef.current;
    if (hLinks.size > 0 && hLinks.has(link.id)) return 2.5;
    return 1.2;
  }, []);

  const linkParticleColorFn = useCallback((link: any) => {
    return getEdgeColor(link.type) + '99';
  }, []);

  // -----------------------------------------------------------------------
  // onRenderFramePost — minimap + tooltip
  // -----------------------------------------------------------------------
  const handleRenderFramePost = useCallback((ctx: CanvasRenderingContext2D, globalScale: number) => {
    // Draw tooltip in graph-space (transform still applied)
    const hovered = hoveredNodeRef.current;
    if (hovered && hovered.x !== undefined) {
      drawTooltip(hovered, ctx, globalScale);
    }

    // Draw minimap in screen-space
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const allNodes = forceGraphData.nodes;
    drawMinimap(allNodes, ctx, ctx.canvas.width, ctx.canvas.height, viewportRef.current);
    ctx.restore();
  }, [forceGraphData.nodes]);

  // -----------------------------------------------------------------------
  // Zoom tracking
  // -----------------------------------------------------------------------
  const handleZoom = useCallback((transform: { k: number; x: number; y: number }) => {
    viewportRef.current = transform;
  }, []);

  // -----------------------------------------------------------------------
  // Force clustering by type
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!graphRef.current || forceGraphData.nodes.length === 0) return;

    const charge = graphRef.current.d3Force('charge');
    if (charge) charge.strength(-100);

    graphRef.current.d3Force('cluster', (alpha: number) => {
      const nodes = forceGraphData.nodes as any[];
      const centroids = new Map<string, { x: number; y: number; count: number }>();
      for (const n of nodes) {
        if (n.x === undefined) continue;
        const c = centroids.get(n.type) || { x: 0, y: 0, count: 0 };
        c.x += n.x; c.y += n.y; c.count++;
        centroids.set(n.type, c);
      }
      centroids.forEach(c => { c.x /= c.count; c.y /= c.count; });
      const strength = 0.025 * alpha;
      for (const n of nodes) {
        const c = centroids.get(n.type);
        if (!c || n.x === undefined) continue;
        n.vx = (n.vx || 0) + (c.x - n.x) * strength;
        n.vy = (n.vy || 0) + (c.y - n.y) * strength;
      }
    });

    graphRef.current.d3ReheatSimulation();
  }, [forceGraphData]);

  // -----------------------------------------------------------------------
  // Zoom-to-fit
  // -----------------------------------------------------------------------
  const handleZoomToFit = useCallback(() => {
    graphRef.current?.zoomToFit(400, 50);
  }, []);

  // -----------------------------------------------------------------------
  // Node detail helpers (for sidebar)
  // -----------------------------------------------------------------------
  const getNodeDetails = (node: ForceGraphNode) => {
    const skip = new Set([
      'id', 'type', 'labels', 'name', 'x', 'y', 'vx', 'vy', 'fx', 'fy',
      'index', '__connections', '__indexColor', 'projectId', 'researchTaskId',
      'discoveredAt',
    ]);
    const extra: Record<string, any> = {};
    Object.entries(node).forEach(([key, value]) => {
      if (!skip.has(key) && value !== undefined && value !== null) {
        extra[key] = value;
      }
    });
    return extra;
  };

  // -----------------------------------------------------------------------
  // Loading / Error / Empty states
  // -----------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={32} className="text-primary animate-spin mr-3" />
        <span className="text-slate-500 text-sm font-medium">Loading knowledge graph...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl border border-red-200 text-sm">
        {error}
      </div>
    );
  }

  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Filter size={28} className="text-slate-400" />
        </div>
        <p className="text-slate-500 text-sm">No graph data available yet. Run a research task to populate the knowledge graph.</p>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Shared sub-components (used in both inline and fullscreen)
  // -----------------------------------------------------------------------
  const statsOverlay = (
    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[60%] pointer-events-none z-10">
      {Object.entries(typeCounts).map(([type, count]) => (
        <span
          key={type}
          className="flex items-center gap-1 px-2 py-1 bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded text-[11px] text-slate-300 font-medium"
        >
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: getNodeConfig(type).color }}
          />
          {isFullscreen ? `${type}: ${count}` : count}
        </span>
      ))}
      <span className="flex items-center gap-1 px-2 py-1 bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded text-[11px] text-slate-400 font-medium">
        {forceGraphData.nodes.length} nodes &middot; {forceGraphData.links.length} edges
      </span>
    </div>
  );

  const toolbar = (
    <div className="absolute top-3 right-3 flex gap-1.5 z-10">
      {hiddenNodeIds.size > 0 && (
        <button
          onClick={ctxShowAll}
          className="flex items-center gap-1.5 px-2.5 py-2 bg-slate-800/80 backdrop-blur-sm border border-amber-600/50 rounded-lg text-[11px] font-semibold text-amber-400 hover:text-amber-300 hover:border-amber-500 transition-colors"
        >
          <Eye size={14} />
          {hiddenNodeIds.size} hidden
        </button>
      )}
      {allowMerge && sessionId && (
        <button
          onClick={() => setShowDuplicatesPanel(p => !p)}
          className={`flex items-center gap-1.5 px-2.5 py-2 bg-slate-800/80 backdrop-blur-sm border rounded-lg text-[11px] font-semibold transition-colors ${
            showDuplicatesPanel
              ? 'border-primary/60 text-primary'
              : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'
          }`}
          title="Find duplicate nodes"
        >
          <Layers size={14} />
          Duplicates
        </button>
      )}
      <button
        onClick={handleZoomToFit}
        className="p-2 bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
        title="Zoom to fit"
      >
        <Scan size={16} />
      </button>
      <button
        onClick={() => setIsFullscreen(f => !f)}
        className="p-2 bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
        title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
      >
        {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
      </button>
    </div>
  );

  const contextMenuEl = contextMenu && (
    <div
      className="absolute z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-2xl py-1 min-w-[200px]"
      style={{ left: contextMenu.x, top: contextMenu.y }}
    >
      <div className="px-3 py-1.5 border-b border-slate-700">
        <p className="text-xs font-bold text-slate-300 truncate">{contextMenu.node.name || contextMenu.node.avatarName || contextMenu.node.story || contextMenu.node.text || contextMenu.node.id}</p>
        <p className="text-[10px] text-slate-500">{getNodeConfig(contextMenu.node.type).label}</p>
      </div>
      <button
        onClick={ctxFocus}
        className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2 transition-colors"
      >
        <Focus size={14} /> Focus on this node
      </button>
      {allowMerge && selectedNode && contextMenu.node.id !== selectedNode.id && (
        <button
          onClick={ctxMergeIntoSelected}
          className="w-full text-left px-3 py-2 text-sm text-amber-300 hover:bg-slate-700 flex items-center gap-2 transition-colors"
        >
          <GitMerge size={14} /> Merge into selected
        </button>
      )}
      <button
        onClick={ctxHide}
        className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2 transition-colors"
      >
        <EyeOff size={14} /> Hide this node
      </button>
      <button
        onClick={ctxShowOnlyConnections}
        className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2 transition-colors"
      >
        <Eye size={14} /> Show only connections
      </button>
      {hiddenNodeIds.size > 0 && (
        <>
          <div className="border-t border-slate-700 my-0.5" />
          <button
            onClick={ctxShowAll}
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2 transition-colors"
          >
            <RotateCcw size={14} /> Show all hidden ({hiddenNodeIds.size})
          </button>
        </>
      )}
    </div>
  );

  const forceGraph = (
    <ForceGraph2D
      ref={graphRef}
      graphData={forceGraphData}
      backgroundColor={isFullscreen ? '#020617' : '#0f172a'}
      nodeCanvasObject={nodeCanvasObject}
      nodeCanvasObjectMode={() => 'replace' as any}
      nodePointerAreaPaint={nodePointerAreaPaint}
      linkColor={linkColorFn}
      linkWidth={linkWidthFn}
      linkCurvature={0.2}
      linkCanvasObject={linkCanvasObject}
      linkCanvasObjectMode={() => 'after' as any}
      linkDirectionalArrowLength={5}
      linkDirectionalArrowRelPos={0.95}
      linkDirectionalArrowColor={linkColorFn}
      linkDirectionalParticles={1}
      linkDirectionalParticleSpeed={0.004}
      linkDirectionalParticleColor={linkParticleColorFn}
      linkDirectionalParticleWidth={2}
      onNodeClick={handleNodeClick}
      onNodeHover={handleNodeHover}
      onNodeRightClick={handleNodeRightClick}
      onBackgroundClick={handleBackgroundClick}
      onBackgroundRightClick={handleBackgroundRightClick}
      onRenderFramePost={handleRenderFramePost}
      onZoom={handleZoom}
      width={graphWidth}
      height={graphHeight}
      cooldownTime={3000}
      d3AlphaDecay={0.02}
    />
  );

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------
  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Controls: Search + Entity Type Filters */}
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
          {/* Search */}
          <div className="relative flex-shrink-0 w-full md:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search nodes by name..."
              className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Entity type filter chips */}
          <div className="flex flex-wrap gap-2">
            {entityTypes.map(type => {
              const config = getNodeConfig(type);
              return (
                <label
                  key={type}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all border ${
                    activeFilters.has(type)
                      ? 'border-slate-300 bg-white shadow-sm'
                      : 'border-slate-200 bg-slate-50 opacity-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={activeFilters.has(type)}
                    onChange={() => toggleFilter(type)}
                    className="sr-only"
                  />
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: config.color }}
                  />
                  {type}
                  {!!typeCounts[type] && (
                    <span className="text-slate-400 ml-0.5">{typeCounts[type]}</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        {/* Merge confirm banner */}
        {allowMerge && mergeTargetNode && selectedNode && (
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
            <AlertTriangle size={16} className="text-amber-500 shrink-0" />
            <span className="flex-1 text-amber-800">
              Merge <strong>{mergeTargetNode.name || mergeTargetNode.id}</strong> into <strong>{selectedNode.name || selectedNode.id}</strong>?
              {' '}Properties and relationships will be combined.
            </span>
            <button
              onClick={handleConfirmMerge}
              disabled={merging}
              className="px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              {merging ? <Loader2 size={12} className="animate-spin" /> : <GitMerge size={12} />}
              {merging ? 'Merging…' : 'Confirm Merge'}
            </button>
            <button
              onClick={() => setMergeTargetNode(null)}
              disabled={merging}
              className="px-3 py-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Graph + Detail Sidebar (inline mode) */}
        {!isFullscreen && (
          <div className="flex gap-4">
            <div
              ref={containerRef}
              className="flex-1 bg-slate-900 border border-slate-700 rounded-xl overflow-hidden relative"
              style={{ minHeight: '500px' }}
            >
              {forceGraph}
              {statsOverlay}
              {toolbar}
              {contextMenuEl}
            </div>

            {/* Duplicates merge panel */}
            {showDuplicatesPanel && sessionId && (
              <DuplicatesMergePanel
                projectId={projectId}
                sessionId={sessionId}
                onClose={() => setShowDuplicatesPanel(false)}
                onMergeComplete={() => { loadGraphData(); }}
              />
            )}

            {/* Detail sidebar */}
            {!showDuplicatesPanel && selectedNode && (
              <div className="w-80 flex-shrink-0 bg-white border border-slate-200 rounded-xl p-5 overflow-y-auto max-h-[650px]">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0 mr-2">
                    {isEditingName ? (
                      <div className="space-y-1.5">
                        <input
                          autoFocus
                          value={editedName}
                          onChange={e => setEditedName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { handleSaveName(); } if (e.key === 'Escape') { setIsEditingName(false); } }}
                          className="w-full text-sm font-bold border border-primary rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                        {saveNameError && <p className="text-xs text-red-600">{saveNameError}</p>}
                        <div className="flex gap-1.5">
                          <button
                            onClick={handleSaveName}
                            disabled={savingName || !editedName.trim()}
                            className="flex items-center gap-1 px-2.5 py-1 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
                          >
                            {savingName ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                            Save
                          </button>
                          <button
                            onClick={() => setIsEditingName(false)}
                            disabled={savingName}
                            className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-1.5 group">
                        <h3 className="text-base font-bold text-slate-900 break-words">{getNodeDisplayName(selectedNode)}</h3>
                        {allowMerge && (
                          <button
                            onClick={handleStartEditName}
                            className="mt-0.5 p-0.5 text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-all rounded shrink-0"
                            title="Edit name"
                          >
                            <Pencil size={13} />
                          </button>
                        )}
                      </div>
                    )}
                    {!isEditingName && (
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getNodeConfig(selectedNode.type).color }}
                        />
                        <span className="text-sm text-slate-500">{selectedNode.type}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setSelectedNode(null); setIsEditingName(false); }}
                    className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors shrink-0"
                  >
                    <X size={16} />
                  </button>
                </div>

                {selectedNode.labels && selectedNode.labels.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Labels</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedNode.labels.map(label => (
                        <span key={label} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-md font-medium">
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Connections</p>
                  <p className="text-sm text-slate-700">{connectionCounts.get(selectedNode.id) || 0} connections</p>
                </div>

                {allowMerge && (
                  <div className="mb-4 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg">
                    <GitMerge size={13} className="text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-700">Right-click another node and choose <strong>Merge into selected</strong> to merge it into this node.</p>
                  </div>
                )}

                {(() => {
                  const details = getNodeDetails(selectedNode);
                  const entries = Object.entries(details);
                  if (entries.length === 0) return null;
                  return (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Properties</p>
                      <div className="space-y-2">
                        {entries.map(([key, value]) => (
                          <div key={key}>
                            <p className="text-xs text-slate-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                            <p className="text-sm text-slate-700 break-words">
                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {graphData && (() => {
                  const connectedEdges = graphData.edges.filter(
                    e => e.source === selectedNode.id || e.target === selectedNode.id
                  );
                  if (connectedEdges.length === 0) return null;
                  const nodeMap = new Map(graphData.nodes.map(n => [n.id, n]));
                  return (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Relationships</p>
                      <div className="space-y-1.5">
                        {connectedEdges.slice(0, 25).map(edge => {
                          const isSource = edge.source === selectedNode.id;
                          const otherNodeId = isSource ? edge.target : edge.source;
                          const otherNode = nodeMap.get(otherNodeId);
                          return (
                            <div
                              key={edge.id}
                              className="flex items-center gap-2 text-xs p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                              onClick={() => {
                                if (otherNode) setSelectedNode({ ...otherNode, __connections: connectionCounts.get(otherNode.id) || 0 });
                              }}
                            >
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: otherNode ? getNodeConfig(otherNode.type).color : '#6b7280' }}
                              />
                              <span className="text-slate-500 flex-shrink-0">
                                {isSource ? '' : '\u2190 '}{edge.type.replace(/_/g, ' ').toLowerCase()}{isSource ? ' \u2192' : ''}
                              </span>
                              <span className="font-medium text-slate-700 truncate">
                                {otherNode ? getNodeDisplayName(otherNode) : otherNodeId}
                              </span>
                            </div>
                          );
                        })}
                        {connectedEdges.length > 25 && (
                          <p className="text-xs text-slate-400 pl-2">
                            +{connectedEdges.length - 25} more relationships
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen overlay — portal-like fixed overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[9999] bg-slate-950 flex flex-col">
          {/* Fullscreen controls bar */}
          <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-slate-900/90 backdrop-blur-sm border-b border-slate-700 z-10 shrink-0">
            {/* Search */}
            <div className="relative w-64">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search nodes..."
                className="w-full pl-8 pr-7 py-1.5 text-xs border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-slate-800 text-slate-200 placeholder-slate-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Entity type filter chips */}
            <div className="flex flex-wrap gap-1.5">
              {entityTypes.map(type => {
                const config = getNodeConfig(type);
                return (
                  <label
                    key={type}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold cursor-pointer transition-all border ${
                      activeFilters.has(type)
                        ? 'border-slate-500 bg-slate-700 text-slate-200 shadow-sm'
                        : 'border-slate-700 bg-slate-800 text-slate-500 opacity-60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={activeFilters.has(type)}
                      onChange={() => toggleFilter(type)}
                      className="sr-only"
                    />
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: config.color }}
                    />
                    {type}
                    {!!typeCounts[type] && (
                      <span className="text-slate-400 ml-0.5">{typeCounts[type]}</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Graph canvas */}
          <div className="relative flex-1 min-h-0">
            {forceGraph}
            {statsOverlay}
            {toolbar}
            {contextMenuEl}
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerResearchGraph;
